import { useState, useEffect, useRef, useCallback, memo, useMemo } from "react";
import { FixedSizeList as List } from "react-window";
import { v4 as uuidv4 } from "uuid";
import useUrlParams from "../../hooks/useUrlParams";
import useDashboard from "../../hooks/useDashboard";
import "../../../command_center/components/center/modals/console/Console.scss";
import { getEnvVar } from "../../../../envUtils";
import { ArrowUp } from "../../../command_center/components/center/modals/console/ConsoleSvg";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";
import { fetchEntries } from "../../../command_center/components/center/modals/console/Utils";
import ShimmerMenuList from "../../../CommonComponents/SimmerEffectDesign/ShimmerMenu";
import useConsole from "../../hooks/useConsole";

const tctmTypeClasses = {
  Beacon: "bg-typeBeacon",
  "Health Metric": "bg-typeHealthMetric",
  TCTM: "bg-typeTCTM",
  TC: "bg-typeTC",
  TM: "bg-typeTM",
  "Invalid TM": "bg-invalidTM",
};

/** Build the CSS class string for a console row */
const getRowClassName = (entry, showCorrespondingTask, highlightedCommands) => {
  const tctmClass = tctmTypeClasses[entry?.["CommandDetails"]?.tctmType] || "";
  if (showCorrespondingTask) return `table__body__row_message ${tctmClass}`;
  const isHighlighted = highlightedCommands.has(entry?.CommandUid) && entry?.type === "Tc";
  return `table__body__row ${isHighlighted ? "highlight-blink" : ""} ${tctmClass}`;
};

const TableCell = memo(({ width, content, children, className }) => (
  <div className={`d-flex align-items-center justify-content-start ${className}`} style={{ width: width }}>
    <CustomToolTip title={content} placement="top">
      <span className="truncate__text">{children ?? content}</span>
    </CustomToolTip>
  </div>
));

const Row = memo(({ index, style, data }) => {
  const { entries, selectedRow, getCommandDetails, show_selected_output_details, consoleTableColumns, setIsOutputModalVisible, isOutputModalVisible, commandDetails, showCorrespondingTask = false, setIsFrozen, setDataCount, listRef, height, isFrozen, frozenData, removeCommandUidForHighlight, highlightedCommands } = data;
  let entry;
  if (showCorrespondingTask) {
    entry = commandDetails?.data?.find(entry => entry?.type !== selectedRow?.type);
  } else {
    entry = entries[index];
  }

  const isSelected = entry?.uniqueId === selectedRow.CommandUid;
  const { dashboard } = useUrlParams();

  const borderStyle =
    entry?.["CommandDetails"]?.tctmType === "TCTM"
      ? { borderLeft: entry.type === "Tc" ? "2px solid #CCF54E" : "2px solid #36F097" }
      : {};

  const prevEntryRef = useRef(null);
  const ROW_HEIGHT = 35;

  function smoothScroll(list, targetOffset, duration = 300) {
    if (!list || typeof list.scrollTo !== "function") return;
    const start = list.state.scrollOffset;
    const change = targetOffset - start;
    const startTime = performance.now();
    function animate(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;
      list.scrollTo(start + change * ease);
      if (elapsed < duration) requestAnimationFrame(animate);
    }
    requestAnimationFrame(animate);
  }

  function safeScrollToIndex(listRef, index) {
    if (!listRef?.current || typeof index !== "number" || index < 0) return;
    const list = listRef.current;
    const outer = list._outerRef || list._listRef || list?.outerRef;
    const itemCount = list.props?.itemCount ?? 0;
    const containerHeight = outer?.clientHeight ?? list.props?.height ?? 0;
    const totalHeight = itemCount * ROW_HEIGHT;
    const desiredOffset = index * ROW_HEIGHT - Math.floor((containerHeight - ROW_HEIGHT) / 2);
    const maxOffset = Math.max(0, totalHeight - containerHeight);
    const scrollOffset = Math.max(0, Math.min(desiredOffset, maxOffset));
    if (typeof list.scrollTo === "function") {
      smoothScroll(listRef.current, scrollOffset);
    } else if (outer && typeof outer.scrollTo === "function") {
      outer.scrollTo({ top: scrollOffset, behavior: "smooth" });
    }
  }

  const handleRowClick = () => {
    removeCommandUidForHighlight(entry?.CommandUid);
    if (prevEntryRef.current !== entry) {
      setIsFrozen(true);
      setDataCount(0);
      setIsOutputModalVisible(true);
      const snapshot = isFrozen ? (frozenData || entries) : entries;
      const listElement = listRef.current?._outerRef;
      if (listElement) {
        const rowTop = index * 35;
        if (rowTop > (height * 0.5 - 150)) {
          const idx = snapshot?.findIndex(e => e?.uniqueId === entry?.uniqueId);
          if (idx != null && idx >= 0) {
            requestAnimationFrame(() => safeScrollToIndex(listRef, idx));
          }
        }
      }
    } else {
      setIsFrozen(false);
      setIsOutputModalVisible(!isOutputModalVisible);
    }
    getCommandDetails(entry);
    show_selected_output_details(entry, entries);
    prevEntryRef.current = entry;
  };

  return (
    <div style={style}>
      <div className={getRowClassName(entry, showCorrespondingTask, highlightedCommands)}>
        <button
          className={`table__body__row__item ${isSelected ? entry?.type === 'Tc' ? "selected-request" : "selected-response" : ''}`}
          onClick={() => { if (!showCorrespondingTask) handleRowClick(); }}
          style={borderStyle}
        >
          {dashboard === "operate" && consoleTableColumns.map((item) => (
            item.isSelected && (
              <TableCell key={item.label} width={`${item?.width}px`} content={item?.value(entry)}>
                {item?.renderIcon?.(entry)}
              </TableCell>
            )
          ))}
          {dashboard !== "operate" && consoleTableColumns
            .filter(item => item.isSelected && item.dashboard !== "operate")
            .map((item) => (
              <TableCell key={item.label} width={`${item?.width}px`} content={item?.value(entry)}>
                {item?.renderIcon?.(entry)}
              </TableCell>
            ))}
        </button>
      </div>
    </div>
  );
});

const ConsoleOutputList = memo(({ height, getCommandDetails, show_selected_output_details, selectedRow, isOutputModalVisible, setIsOutputModalVisible, consoleTableColumns, setEntries, entries, allFilters, setIsFrozen, isFrozen, dataCount, setDataCount, setSelectedRow }) => {
  const { selected_element } = useDashboard();
  const { trueTwinId, elementId } = selected_element || {};

  const { scenario_id } = useUrlParams();
  const { highlightedCommands, removeCommandUidForHighlight } = useConsole();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [commandLoading, setCommandLoading] = useState(false);
  const loadingRef = useRef(false);
  const listRef = useRef(null);
  const cursorRef = useRef("");
  const isAtTopRef = useRef(true);
  const [isDataFound, setIsDataFound] = useState(false);
  const [frozenData, setFrozenData] = useState(null);
  const isWsLoadingRef = useRef(false);
  const isWsPendingRef = useRef(false);
  const loadLatestEntriesRef = useRef(null);
  const isFrozenRef = useRef(isFrozen);
  const entriesRef = useRef(entries);

  useEffect(() => { isFrozenRef.current = isFrozen; }, [isFrozen]);
  useEffect(() => { entriesRef.current = entries; }, [entries]);

  useEffect(() => {
    const handleFilterUpdate = (e) => loadLatestEntries(e.detail, true, false);
    window.addEventListener('postFilters', handleFilterUpdate);
    return () => window.removeEventListener('postFilters', handleFilterUpdate);
  }, []);



  const loadLatestEntries = useCallback(async (filters, isFilterApplied, fromWs) => {
    if (isFrozenRef.current) return;
    loadingRef.current = true;
    if (fromWs && isWsLoadingRef.current) {
      isWsPendingRef.current = true;
      return;
    }
    if (fromWs) isWsLoadingRef.current = true;
    try {
      const { data, cursor } = await fetchEntries(elementId, trueTwinId, scenario_id, "", filters, isFilterApplied, setCommandLoading, false);
      setEntries(data);
      if (data) setIsDataFound(true);
      cursorRef.current = cursor;
      sessionStorage.setItem('globalCursor', cursor);
    } finally {
      loadingRef.current = false;
      if (fromWs) {
        isWsLoadingRef.current = false;
        if (isWsPendingRef.current) {
          isWsPendingRef.current = false;
          loadLatestEntries(allFiltersRef.current, false, true);
        }
      }
    }
  }, [elementId, trueTwinId, scenario_id]);

  useEffect(() => { loadLatestEntriesRef.current = loadLatestEntries; }, [loadLatestEntries]);

  const loadMoreEntries = useCallback(async (filters, isFilterApplied) => {
    if (loadingRef.current || !cursorRef.current) return;
    loadingRef.current = true;
    try {
      const { data, cursor } = await fetchEntries(elementId, trueTwinId, scenario_id, cursorRef.current, filters, isFilterApplied, setCommandLoading, true);
      setEntries((prev) => [...prev, ...data]);
      cursorRef.current = cursor;
      sessionStorage.setItem('globalCursor', cursor);
    } finally {
      loadingRef.current = false;
    }
  }, [elementId, trueTwinId, scenario_id]);

  const handleScroll = useCallback(
    ({ scrollOffset, scrollDirection }) => {
      setShowScrollTop(scrollOffset > 10);
      const list = listRef.current;
      if (!list) return;
      const totalHeight = (entriesRef.current?.length || 0) * list.props.itemSize;
      const bottomThreshold = 5;
      if (!isFrozenRef.current) {
        if (scrollDirection === "forward" && scrollOffset + list.props.height >= totalHeight - bottomThreshold) {
          loadMoreEntries(allFiltersRef.current, false);
        } else if (scrollDirection === "backward" && scrollOffset === 0) {
          loadLatestEntries(allFiltersRef.current, false, false);
        }
      }
      isAtTopRef.current = scrollOffset === 0;
    },
    [loadMoreEntries, loadLatestEntries]
  );

  const allFiltersRef = useRef(allFilters);
  useEffect(() => { allFiltersRef.current = allFilters; }, [allFilters]);

  useEffect(() => {
    const token = sessionStorage.getItem("authentication");
    if (!trueTwinId || !token) return;
    const sessionId = uuidv4();
    const ws = new WebSocket(`${getEnvVar("REACT_APP_WEBSOCKET_BACKEND_BASE_URL")}groundstation/websocketstatus?truetwinId=${trueTwinId}&sessionId=${sessionId}`);
    ws.onopen = () => ws.send(JSON.stringify({ type: "authorization", token }));
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg?.count > 0 && isFrozenRef.current) setDataCount((prev) => (prev ?? 0) + msg.count);
      if (isAtTopRef.current && !isFrozenRef.current) loadLatestEntriesRef.current?.(allFiltersRef.current, false, true);
    };
    ws.onerror = console.error;
    return () => ws.close();
  }, [trueTwinId, elementId]);

  useEffect(() => {
    loadLatestEntries(allFilters, true, false);
  }, [trueTwinId, elementId]);

  const handleBtnClick = () => {
    loadLatestEntries(allFilters, false, false);
    listRef.current?.scrollTo(0);
  };

  useEffect(() => {
    if (isFrozen && !frozenData) setFrozenData(entries);
    else if (!isFrozen && frozenData) setFrozenData(null);
  }, [isFrozen, entries, frozenData]);

  useEffect(() => {
    if (!isFrozen) {
      loadLatestEntries(allFiltersRef.current, true, false);
      setIsOutputModalVisible(false);
      setSelectedRow({});
      listRef.current?.scrollTo(0);
    }
  }, [isFrozen]);

  const itemData = useMemo(
    () => ({
      entries: isFrozen ? frozenData || [] : entries,
      selectedRow,
      getCommandDetails,
      show_selected_output_details,
      consoleTableColumns,
      setIsOutputModalVisible,
      isOutputModalVisible,
      setIsFrozen,
      setDataCount,
      listRef,
      height,
      isFrozen,
      frozenData,
      removeCommandUidForHighlight,
      highlightedCommands,
    }),
    [entries, selectedRow, getCommandDetails, show_selected_output_details, consoleTableColumns, setIsFrozen, frozenData, setDataCount, height, isFrozen, removeCommandUidForHighlight, highlightedCommands]
  );

  return (
    <>
      {!isDataFound && <ShimmerMenuList />}
      <div className={`output__div__message__container ${((entries?.length === 0 || !entries) && isDataFound) ? 'd-flex' : 'd-none'}`}>
        No data found.
      </div>
      <List
        ref={listRef}
        itemData={itemData}
        height={height}
        itemCount={entries?.length}
        itemSize={35}
        width="100%"
        onScroll={handleScroll}
        overscanCount={10}
        className={`row_change_position_console_table modal__scrollbar__white__vertical ${isOutputModalVisible ? "row_change_position_console_table_shortend" : ""}`}
        style={{ overflowY: "auto" }}
      >
        {Row}
      </List>
      <button
        onClick={handleBtnClick}
        title="Scroll to top"
        className={`scroll-to-top ${(showScrollTop && !isFrozen) ? "" : "hidden"} ${isOutputModalVisible ? "" : "scroll-to-top-middle"}`}
      >
        <ArrowUp />
      </button>
    </>
  );
});

export { ConsoleOutputList, Row };
